import { createFocusTrap, type FocusTrap } from 'focus-trap'
import { nextTick, type ObjectDirective, type DirectiveBinding } from 'vue'

type FocusTrapElement =
  | string
  | HTMLElement
  | SVGElement
  | (string | HTMLElement | SVGElement)[]

type FocusTrapConfig = {
  enabled: boolean
  enableInitialFocus?: boolean
}

type FocusTrapValue = boolean | FocusTrapConfig | undefined | null

/**
 * constants
 */
const PREFIX = 'FocusTrap'

const createTrap = (
  element: FocusTrapElement,
  enableInitialFocus: boolean = true,
): FocusTrap => {
  return createFocusTrap(element, {
    escapeDeactivates: false,
    allowOutsideClick: true,
    initialFocus: enableInitialFocus
      ? (): HTMLElement | false => {
          if (Array.isArray(element)) {
            return (
              element.find(
                (el): el is HTMLInputElement => el instanceof HTMLInputElement,
              ) ?? false
            )
          }

          if (element instanceof HTMLInputElement) {
            return element
          }

          if (element instanceof HTMLElement) {
            return element.querySelector('input') ?? false
          }

          return false
        }
      : (): HTMLButtonElement | false => {
          // Fallback to button (most likely the close button) if
          // we do not want initial focus on input
          if (Array.isArray(element)) {
            return (
              element.find(
                (el): el is HTMLButtonElement =>
                  el instanceof HTMLButtonElement,
              ) ?? false
            )
          }

          if (element instanceof HTMLButtonElement) {
            return element
          }

          if (element instanceof HTMLElement) {
            return element.querySelector('button') ?? false
          }

          return false
        },
  })
}

const setupFocusTrap = (
  el: HTMLElement & { [PREFIX]?: { trap: FocusTrap } },
  binding: DirectiveBinding<FocusTrapValue, string, string>,
): void => {
  const focusTrapElement = binding.arg ? [el, ...binding.arg] : el

  let enabled = true
  let enableInitialFocus = true

  if (typeof binding.value === 'boolean') {
    enabled = binding.value
    enableInitialFocus = !binding.modifiers?.noInitialFocus
  } else if (typeof binding.value === 'object' && binding.value !== null) {
    enabled = binding.value.enabled
    enableInitialFocus = binding.value.enableInitialFocus ?? true
  } else if (binding.value === undefined || binding.value === null) {
    enabled = true
    enableInitialFocus = !binding.modifiers?.noInitialFocus
  }

  if (!enabled) {
    return
  }

  const trap = createTrap(focusTrapElement, enableInitialFocus)

  el[PREFIX] = {
    trap,
  }
}

const activateFocusTrap = (
  el: HTMLElement & { [PREFIX]?: { trap: FocusTrap } },
): void => {
  nextTick(() => {
    try {
      el[PREFIX]?.trap.activate()
    } catch (e) {
      console.error(e)
    }
  })
}

const focusTrap: ObjectDirective<
  HTMLElement & {
    [PREFIX]?: {
      trap: FocusTrap
    }
  },
  FocusTrapValue
> = {
  mounted(el, binding) {
    let enabled = true
    if (typeof binding.value === 'boolean') {
      enabled = binding.value
    } else if (typeof binding.value === 'object' && binding.value !== null) {
      enabled = binding.value.enabled
    }

    if (!el[PREFIX] && enabled) {
      setupFocusTrap(el, binding)
    }

    if (el[PREFIX]?.trap && enabled) {
      activateFocusTrap(el)
    }
  },
  // Call when the directive is updated
  updated(el, binding) {
    let enabled = true
    if (typeof binding.value === 'boolean') {
      enabled = binding.value
    } else if (typeof binding.value === 'object' && binding.value !== null) {
      enabled = binding.value.enabled
    }

    if (el[PREFIX]?.trap) {
      if (enabled) {
        activateFocusTrap(el)
      } else {
        try {
          el[PREFIX].trap.deactivate()
        } catch (e) {
          console.error(e)
        }
      }
    } else if (enabled) {
      // Create trap if it doesn't exist and value is true
      setupFocusTrap(el, binding)
      activateFocusTrap(el)
    }
  },
  beforeUnmount(el) {
    if (el[PREFIX]?.trap) {
      el[PREFIX].trap.deactivate()
    }
  },
}

export default focusTrap
